import {
  Avatar,
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import {
  Controller,
  useForm,
} from "react-hook-form";

import { CurrencySelect } from "../../../components/currencies/CurrencySelect";

const fieldSx = {
  "& .MuiInputBase-root": {
    minHeight: 38,
    borderRadius: 0,
    fontSize: "0.72rem",
  },

  "& .MuiInputLabel-root": {
    fontSize: "0.72rem",
  },

  "& .MuiFormHelperText-root": {
    mx: 0,
    fontSize: "0.63rem",
  },
};

function initials(value) {
  return String(value || "WR")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

export function ShopWifiPackageRuleForm({
  rule,
  shop,
  packages,
  busy,
  onCancel,
  onSubmit,
}) {
  const {
    control,
    handleSubmit,
    formState: {
      errors,
    },
  } = useForm({
    defaultValues: {
      wifi_package_id:
        rule?.wifi_package_id ||
        rule?.wifi_package?.id ||
        "",

      currency:
        rule?.currency ||
        "",

      minimum_amount:
        rule?.minimum_amount ??
        0,

      is_active:
        rule?.is_active ??
        true,
    },
  });

  const submit = (values) => {
    onSubmit({
      wifi_package_id:
        values.wifi_package_id,

      currency:
        String(
          values.currency,
        ).toUpperCase(),

      minimum_amount:
        Number(
          values.minimum_amount,
        ),

      is_active:
        Boolean(
          values.is_active,
        ),
    });
  };

  const shopName =
    shop?.shop_name ||
    rule?.shop?.shop_name ||
    "Shop";

  const shopCode =
    shop?.shop_code ||
    rule?.shop?.shop_code ||
    "";

  return (
    <Box
      component="form"
      onSubmit={
        handleSubmit(submit)
      }
    >
      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            md: "220px 1fr",
          },

          gap: 2,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 0,
            textAlign: "center",
          }}
        >
          <Avatar
            sx={{
              width: 92,
              height: 92,
              mx: "auto",

              bgcolor:
                "#EEF3FF",

              color:
                "#2F5BEA",

              fontWeight:
                850,
            }}
          >
            {initials(
              shopName,
            )}
          </Avatar>

          <Typography
            sx={{
              mt: 1.4,

              fontSize:
                "0.78rem",

              fontWeight:
                800,
            }}
          >
            {shopName}
          </Typography>

          {shopCode ? (
            <Typography
              sx={{
                mt: 0.3,

                color:
                  "#667085",

                fontSize:
                  "0.65rem",

                fontWeight:
                  700,
              }}
            >
              {shopCode}
            </Typography>
          ) : null}

          <Typography
            sx={{
              mt: 1,

              color:
                "#7A8696",

              fontSize:
                "0.64rem",

              lineHeight:
                1.5,
            }}
          >
            {rule
              ? "Update the Wi-Fi package rule for this shop."
              : "Configure a Wi-Fi package rule for this shop."}
          </Typography>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 0,
          }}
        >
          <Typography
            sx={{
              mb: 1.4,

              fontSize:
                "0.69rem",

              fontWeight:
                850,

              textTransform:
                "uppercase",

              letterSpacing:
                "0.07em",
            }}
          >
            Rule configuration
          </Typography>

          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr",
              },

              gap: 1.2,
            }}
          >
            <Controller
              name="wifi_package_id"
              control={control}
              rules={{
                required:
                  "Wi-Fi package is required.",
              }}
              render={({
                field,
              }) => (
                <TextField
                  {...field}
                  select
                  label="Wi-Fi package"
                  fullWidth
                  disabled={busy}
                  error={Boolean(
                    errors
                      .wifi_package_id,
                  )}
                  helperText={
                    errors
                      .wifi_package_id
                      ?.message
                  }
                  sx={fieldSx}
                >
                  <MenuItem value="">
                    <em>
                      Select package
                    </em>
                  </MenuItem>

                  {packages.map(
                    (item) => (
                      <MenuItem
                        key={
                          item.id
                        }
                        value={
                          item.id
                        }
                      >
                        {item.name}
                      </MenuItem>
                    ),
                  )}
                </TextField>
              )}
            />

            <Controller
              name="currency"
              control={control}
              rules={{
                required:
                  "Currency is required.",
              }}
              render={({
                field,
              }) => (
                <CurrencySelect
                  value={
                    field.value
                  }
                  onChange={
                    field.onChange
                  }
                  error={Boolean(
                    errors.currency,
                  )}
                  helperText={
                    errors.currency
                      ?.message
                  }
                  disabled={busy}
                />
              )}
            />

            <Controller
              name="minimum_amount"
              control={control}
              rules={{
                required:
                  "Minimum amount is required.",

                min: {
                  value: 0,

                  message:
                    "Amount cannot be negative.",
                },
              }}
              render={({
                field,
              }) => (
                <TextField
                  {...field}
                  type="number"
                  label="Minimum amount"
                  fullWidth
                  disabled={busy}
                  inputProps={{
                    min: 0,
                    step: "0.01",
                  }}
                  error={Boolean(
                    errors
                      .minimum_amount,
                  )}
                  helperText={
                    errors
                      .minimum_amount
                      ?.message
                  }
                  sx={fieldSx}
                />
              )}
            />
          </Box>

          <Controller
            name="is_active"
            control={control}
            render={({
              field,
            }) => (
              <FormControlLabel
                sx={{
                  mt: 1.2,
                }}
                control={
                  <Switch
                    size="small"
                    disabled={
                      busy
                    }
                    checked={Boolean(
                      field.value,
                    )}
                    onChange={(
                      _,
                      checked,
                    ) =>
                      field.onChange(
                        checked,
                      )
                    }
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize:
                        "0.7rem",

                      fontWeight:
                        700,
                    }}
                  >
                    Active rule
                  </Typography>
                }
              />
            )}
          />
        </Paper>
      </Box>

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
        sx={{
          mt: 2,
        }}
      >
        <Button
          color="inherit"
          variant="outlined"
          onClick={onCancel}
          disabled={busy}
          sx={{
            borderRadius: 0,
          }}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          variant="contained"
          disabled={
            busy ||
            !packages.length
          }
          sx={{
            borderRadius: 0,
          }}
        >
          {busy
            ? "Saving..."
            : rule
              ? "Save changes"
              : "Create rule"}
        </Button>
      </Stack>
    </Box>
  );
}